import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { Row } from "@/components";
import { FormCurrencyInput } from "@/components/form/FormCurrencyInput";
import { FormDateInput } from "@/components/form/FormDateInput";
import { FormNumberInput } from "@/components/form/FormNumberInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useUpdatePlacement } from "@/hooks/mutations/useUpdatePlacement";
import { useSetCandidacyInAllLists } from "@/hooks/sets/useSetCandidacyInAllLists";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { useSetPlacement } from "@/hooks/sets/useSetPlacement";
import { useSetRequisition } from "@/hooks/sets/useSetRequisition";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { mutations } from "@/lib/mutations";
import { requiredDateSchema } from "@/lib/schemas";
import {
  CandidacyPlacement,
  CandidacyStatus,
  PlacementStatus,
  Requisition,
  RequisitionStatus,
  RevenueType,
} from "@/types";

const employedSchema = z.object({
  backfillGuaranteeDays: z.coerce.number().min(1, "Must be greater than 0"),
  dateSetToStart: requiredDateSchema,
  dateStarted: requiredDateSchema,
  revenueEarned: z.coerce.number().min(0, "Must be greater than 0").nullable(),
  revenueType: z.nativeEnum(RevenueType, { message: "Required" }),
});

export const EmployedDialog = ({
  open,
  onOpenChange,
  placements,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placements: CandidacyPlacement[];
}) => {
  /* Contexts */
  const { candidacyId, job } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const { data: candidacyInfo } = useGetCandidacyInfo({ candidacyId });

  /* React Hooks */
  const [selectedRequisition, setSelectedRequisition] =
    useState<Requisition | null>(null);

  /* Database Updates */
  // Updates
  const { mutate: updatePlacement } = useUpdatePlacement();

  /* Cache Updates */
  // Sets
  const setCandidacyInAllLists = useSetCandidacyInAllLists();
  const setCandidacyInfo = useSetCandidacyInfo();
  const setPlacement = useSetPlacement();
  const setRequisition = useSetRequisition();

  const form = useForm<z.infer<typeof employedSchema>>({
    resolver: zodResolver(
      employedSchema.refine(
        (data) =>
          // The only acception to an empty revenue earned when filling out the employed dialog is if the requisition is a backfill, since all backfills are for $0.
          selectedRequisition?.requisitionStatusChanges[0]?.fromStatus ===
          RequisitionStatus.BACKFILL
            ? true
            : !!data.revenueEarned,
        {
          message: "Revenue earned is required!",
          path: ["revenueEarned"],
        },
      ),
    ),
    defaultValues: {
      backfillGuaranteeDays: undefined,
      dateSetToStart: "",
      dateStarted: "",
      revenueEarned: undefined,
      revenueType: job.revenueType,
    },
  });

  // Prefill from most recent placement
  useEffect(() => {
    if (placements.length === 0) return;

    const mostRecentPlacement = placements[0];

    setSelectedRequisition(mostRecentPlacement.requisition);
    if (job.backfillGuaranteeDays)
      form.setValue("backfillGuaranteeDays", Number(job.backfillGuaranteeDays));
    form.setValue(
      "dateSetToStart",
      dates.formatIsoDateForUser(mostRecentPlacement.dateSetToStart) ?? "",
    );
    form.setValue("revenueEarned", mostRecentPlacement.revenueEarned);
  }, [form, job, placements]);

  const handleSubmit = (data: z.infer<typeof employedSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Mark employed",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    if (placements.length === 0) return;

    const mostRecentPlacement = placements[0];

    mutations.updatePlacement({
      placementId: mostRecentPlacement.id,
      setCandidacyInfo,
      setPlacement,
      setRequisition,
      updatePlacement,
      updatePlacementRequest: {
        dateSetToStart: dates.convertStringDateToIso(data.dateSetToStart),
        dateStarted: dates.convertStringDateToIso(data.dateStarted),
        revenueEarned: data.revenueEarned,
        status: PlacementStatus.EMPLOYED,
      },
      onSuccess: () => {
        setCandidacyInAllLists({
          candidacyId,
          lastCandidacyStatusChange: {
            createdAt: new Date().toISOString(),
            fromStatus: candidacyInfo?.status ?? null,
            toStatus: CandidacyStatus.EMPLOYED,
            statusChangeReason: null,
            byAgent: false,
          },
        });
        setCandidacyInfo({
          candidacyId,
          status: CandidacyStatus.EMPLOYED,
          lastStatusChangeByAgent: false,
        });
        form.reset();
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog
      onOpenChange={(open) => {
        onOpenChange(open);
      }}
      open={open}
    >
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mark candidate as employed</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormDateInput
              fieldName="dateSetToStart"
              form={form}
              label="Date set to start"
            />
            <FormDateInput
              fieldName="dateStarted"
              form={form}
              label="Date started"
            />
            <FormCurrencyInput
              disabled={
                !selectedRequisition ||
                selectedRequisition.status === RequisitionStatus.BACKFILL ||
                job.revenueType === RevenueType.PERCENT
              }
              fieldName="revenueEarned"
              form={form}
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              label={`Revenue earned (${job.revenueType === RevenueType.PERCENT ? `${job.percentOfSalary ?? 0}% of annualized salary` : "fixed"})`}
              placeholder={
                selectedRequisition &&
                selectedRequisition.requisitionStatusChanges[0]?.fromStatus ===
                  RequisitionStatus.BACKFILL
                  ? "$0"
                  : undefined
              }
            />
            <FormNumberInput
              disabled
              fieldName="backfillGuaranteeDays"
              form={form}
              label="Backfill guarantee days (must be edited from job form)"
            />
            <Row className="justify-end">
              <Button className="rounded-full" type="submit">
                Submit
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
